import { useCallback, useEffect, useMemo, useState } from 'react';
import { endAdminImpersonation } from '../../../services/modules/superadmin/superadminApi';

export const SUPPORT_SESSION_STORAGE_KEY = 'adlitics_support_session';

export type SupportSessionState = {
    sessionId: string;
    organizationId: string;
    organizationName: string;
    expiresAt: string;
};

function readStoredSession(): SupportSessionState | null {
    try {
        const raw = sessionStorage.getItem(SUPPORT_SESSION_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as SupportSessionState;
        if (
            !parsed?.sessionId ||
            !parsed?.organizationId ||
            !parsed?.organizationName ||
            !parsed?.expiresAt
        ) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

export function isSupportSessionExpired(expiresAt: string): boolean {
    return Date.parse(expiresAt) <= Date.now();
}

export function persistSupportSession(session: SupportSessionState): void {
    sessionStorage.setItem(SUPPORT_SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function clearSupportSessionStorage(): void {
    sessionStorage.removeItem(SUPPORT_SESSION_STORAGE_KEY);
}

export function useSupportSession() {
    const [session, setSession] = useState<SupportSessionState | null>(() => readStoredSession());

    const refresh = useCallback(() => {
        setSession(readStoredSession());
    }, []);

    const clear = useCallback(() => {
        clearSupportSessionStorage();
        setSession(null);
    }, []);

    const isActive = useMemo(() => {
        if (!session) return false;
        return !isSupportSessionExpired(session.expiresAt);
    }, [session]);

    useEffect(() => {
        if (!session || isActive) return;

        void (async () => {
            try {
                await endAdminImpersonation(session.sessionId);
            } catch {
                // Best-effort cleanup when TTL expired locally
            } finally {
                clear();
            }
        })();
    }, [session, isActive, clear]);

    return {
        session: isActive ? session : null,
        isActive,
        persist: (next: SupportSessionState) => {
            persistSupportSession(next);
            setSession(next);
        },
        clear,
        refresh,
    };
}
